import * as React from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { DocumentTitle, PageHeader, Link } from "components/common";
import { Col, Row, MenuItem } from "components/bootstrap";
import { MoreActions } from "components/common/EntityDataTable";
import PaginatedEntityTable from "components/common/PaginatedEntityTable";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { LICENSE_SUBJECTS } from "license/constants";
import LicenseCheck from "license/LicenseCheck";
import usePermissions from "hooks/usePermissions";
import { fetchWatchlists } from "watchlist/hooks/useWatchlistsV2";
import SetDefaultTtlModal from "watchlist/components/SetDefaultTtlModal";
import WatchlistRoutes from "watchlist/Routes";
const WATCHLIST_TABLE_ID = "watchlists";
const EXTERNAL_SEARCH = { query: "" };
const COLUMNS = ["name", "description", "entry_count", "default_ttl_seconds"];
const getWatchlistAttributes = () => [
  { id: "name", title: i18n.t("watchlists_overview_page.name", { ns: "enterprise_search" }), sortable: false },
  {
    id: "description",
    title: i18n.t("watchlists_overview_page.description_column", { ns: "enterprise_search" }),
    sortable: false
  },
  {
    id: "entry_count",
    title: i18n.t("watchlists_overview_page.entries", { ns: "enterprise_search" }),
    sortable: false
  },
  {
    id: "default_ttl_seconds",
    title: i18n.t("watchlists_overview_page.default_ttl", { ns: "enterprise_search" }),
    sortable: false
  }
];
const DEFAULT_LAYOUT = {
  entityTableId: WATCHLIST_TABLE_ID,
  defaultPageSize: 20,
  defaultSort: { attributeId: "name", direction: "asc" },
  defaultDisplayedAttributes: ["name", "entry_count", "default_ttl_seconds"],
  defaultColumnOrder: COLUMNS
};
const TTL_UNITS = ["DAYS", "HOURS", "MINUTES", "SECONDS"];
const formatTtl = (seconds) => {
  if (!seconds || seconds <= 0) {
    return i18n.t("watchlists_overview_page.no_expiry", { ns: "enterprise_search" });
  }
  const { duration, unit } = extractDurationAndUnit(seconds * 1e3, TTL_UNITS);
  return i18n.t(`watchlists_overview_page.ttl_${unit.toLowerCase()}`, { ns: "enterprise_search", count: duration });
};
const columnRenderers = {
  attributes: {
    name: {
      renderCell: (_name, watchlist) => /* @__PURE__ */ React.createElement(Link, { to: WatchlistRoutes.DETAILS(watchlist.id) }, watchlist.name)
    },
    entry_count: {
      renderCell: (_entryCount, watchlist) => /* @__PURE__ */ React.createElement("span", null, watchlist.entry_count),
      staticWidth: 80
    },
    default_ttl_seconds: {
      renderCell: (_ttl, watchlist) => /* @__PURE__ */ React.createElement("span", null, formatTtl(watchlist.default_ttl_seconds)),
      staticWidth: 100
    }
  }
};
const watchlistsKeyFn = (searchParams) => ["watchlists", "v2", "overview", searchParams];
const fetchWatchlistsPaginated = (searchParams) => fetchWatchlists().then((all) => {
  var _a;
  const query = ((_a = searchParams.query) != null ? _a : "").trim().toLowerCase();
  const filtered = query ? all.filter(
    (watchlist) => [watchlist.name, watchlist.description, watchlist.type].some(
      (field) => (field != null ? field : "").toLowerCase().includes(query)
    )
  ) : all;
  const start = (searchParams.page - 1) * searchParams.pageSize;
  const list = filtered.slice(start, start + searchParams.pageSize);
  return {
    list,
    pagination: { total: filtered.length },
    attributes: getWatchlistAttributes(),
    meta: null
  };
});
const WatchlistsOverviewPage = () => {
  const { isPermitted } = usePermissions();
  const canEdit = isPermitted("watchlist:edit");
  const queryClient = useQueryClient();
  const [ttlTarget, setTtlTarget] = useState(null);
  const { t } = useTranslation("enterprise_search");
  const renderEntityActions = (watchlist) => /* @__PURE__ */ React.createElement(MoreActions, null, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: () => setTtlTarget(watchlist) }, t("watchlists_overview_page.set_default_ttl")));
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: t("watchlists_overview_page.document_title") }, /* @__PURE__ */ React.createElement(PageHeader, { title: t("watchlists_overview_page.title") }, /* @__PURE__ */ React.createElement("span", null, t("watchlists_overview_page.description"))), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(LicenseCheck, { licenseSubject: LICENSE_SUBJECTS.security, featureName: "watchlists", hideChildren: true }, /* @__PURE__ */ React.createElement(
    PaginatedEntityTable,
    {
      humanName: t("watchlists_overview_page.human_name"),
      tableLayout: DEFAULT_LAYOUT,
      fetchEntities: fetchWatchlistsPaginated,
      keyFn: watchlistsKeyFn,
      entityAttributesAreCamelCase: false,
      columnRenderers,
      entityActions: canEdit ? renderEntityActions : void 0,
      externalSearch: EXTERNAL_SEARCH
    }
  )))), ttlTarget && /* @__PURE__ */ React.createElement(
    SetDefaultTtlModal,
    {
      watchlistId: ttlTarget.id,
      watchlistName: ttlTarget.name,
      currentTtlSeconds: ttlTarget.default_ttl_seconds,
      onClose: () => setTtlTarget(null),
      onUpdated: () => queryClient.invalidateQueries({ queryKey: ["watchlists", "v2"] })
    }
  ));
};
export default WatchlistsOverviewPage;
